import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  ListItemText,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';
import { useAtomValue } from 'jotai';
import { useCallback, useState } from 'react';

import { caseDetailsAtom, useSendCaseToAI } from '@/pages/Case/Case.store';

import {
  type CaseAISubmitDialogProps,
  CaseAISubmitType
} from './CaseAISubmitDialog.types';

const CaseAISubmitDialog = ({ open, setOpen }: CaseAISubmitDialogProps) => {
  const [submitType, setSubmitType] = useState<CaseAISubmitType>(
    CaseAISubmitType.CONTINUE_ANALYSIS
  );
  const [prompt, setPrompt] = useState('');
  const caseDetails = useAtomValue(caseDetailsAtom);
  const { sendCaseToAI, isLoadingSendCaseToAI } = useSendCaseToAI();

  const promptRef = useCallback((node: HTMLTextAreaElement | null) => {
    if (node) {
      setTimeout(() => node.focus(), 0);
    }
  }, []);

  const handleSubmit = () => {
    sendCaseToAI(
      {
        caseId: caseDetails?.soc_id,
        submitType,
        prompt:
          submitType === CaseAISubmitType.CONTINUE_ANALYSIS ? prompt : undefined
      },
      {
        onSuccess: () => {
          setOpen(false);
          setSubmitType(CaseAISubmitType.CONTINUE_ANALYSIS);
          setPrompt('');
        }
      }
    );
  };

  const handleCancel = () => {
    setOpen(false);
    setSubmitType(CaseAISubmitType.CONTINUE_ANALYSIS);
    setPrompt('');
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Submit Case to AI</DialogTitle>
      <DialogContent sx={{ py: 2 }}>
        <Typography sx={{ mb: 1, mt: 2 }}>
          Choose how to resubmit this case to Kanati for analysis.
        </Typography>
        <FormControl fullWidth>
          <RadioGroup
            sx={{ gap: '1rem', pl: 2 }}
            value={submitType}
            onChange={(e) => setSubmitType(e.target.value as CaseAISubmitType)}
          >
            <FormControlLabel
              value={CaseAISubmitType.NEW_ANALYSIS}
              control={
                <Radio sx={{ alignSelf: 'flex-start', mt: '0.25rem' }} />
              }
              label={
                <ListItemText
                  slotProps={{
                    primary: {
                      sx: { fontWeight: 'bold' }
                    }
                  }}
                  primary="Run New Analysis"
                  secondary="Clear prior results and submit the case for a fresh AI analysis."
                />
              }
            />
            <FormControlLabel
              value={CaseAISubmitType.CONTINUE_ANALYSIS}
              control={
                <Radio sx={{ alignSelf: 'flex-start', mt: '0.25rem' }} />
              }
              label={
                <ListItemText
                  slotProps={{
                    primary: {
                      sx: { fontWeight: 'bold' }
                    }
                  }}
                  primary="Continue Existing Analysis"
                  secondary="Preserve existing research and provide additional instructions to continue the investigation."
                />
              }
            />
          </RadioGroup>
        </FormControl>
        {submitType === CaseAISubmitType.CONTINUE_ANALYSIS && (
          <Box sx={{ mt: 0, ml: 4.75 }}>
            <TextField
              inputRef={promptRef}
              fullWidth
              minRows={3}
              multiline
              placeholder="Provide additional context or instructions for Kanati..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={
            isLoadingSendCaseToAI ||
            (submitType === CaseAISubmitType.CONTINUE_ANALYSIS &&
              !prompt.trim())
          }
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CaseAISubmitDialog;
